"use client";

import { useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { SignedIn, SignedOut } from "@clerk/nextjs";
import { Check, PartyPopper, Trophy, Users, X } from "lucide-react";
import { useTranslations } from "next-intl";
import type { FeaturedAwardShow, FeaturedShowBallot } from "@award-party/shared";
import { BrandMark } from "@/components/brand-mark";
import {
  getHeroPreviewContent,
  getInteractiveHeroPreview,
  heroPreviewCreatePartyHref,
  heroPreviewMonogram,
  HERO_PREVIEW_YOU_BG,
  type HeroPreviewContent,
  type HeroPreviewNominee,
  type HeroPreviewPlayer,
  type HeroPreviewReaction,
} from "@/lib/hero-preview";

export function HeroProductPreview({
  show,
  createPartyPath = "/parties/create",
}: {
  show: Pick<
    FeaturedAwardShow,
    "id" | "name" | "displayName" | "year" | "startTime"
  > & {
    ballot?: FeaturedShowBallot | null;
  };
  createPartyPath?: string;
}) {
  const t = useTranslations("Home");
  const preview = getHeroPreviewContent(show);
  const youName = t("preview.you");
  const you = {
    name: youName,
    initial: youName.charAt(0) || "Y",
    bg: HERO_PREVIEW_YOU_BG,
  };
  const [pickedName, setPickedName] = useState<string | null>(null);
  const [saveNudgeDismissed, setSaveNudgeDismissed] = useState(false);
  const [saveNudgeOpen, setSaveNudgeOpen] = useState(false);
  const interactive = getInteractiveHeroPreview(preview, pickedName, you);
  const announced = preview.nominees.find((nominee) => nominee.selected);
  const [maya, alex, jordan] = preview.reactions;
  const youIsLeading = interactive.leaderboard[0]?.name === you.name;
  const leadReaction: HeroPreviewReaction | undefined = youIsLeading
    ? { name: you.name, initial: you.initial, bg: you.bg, kind: "tookTheLead" }
    : maya;

  return (
    <div
      className="group marketing-reveal relative mx-auto mt-4 w-full max-w-5xl lg:mt-0 lg:max-w-none"
      role="region"
      aria-label={t("preview.sampleBallot")}
    >
      <div
        className="pointer-events-none absolute left-1/2 top-[40%] h-[70%] w-[120%] -translate-x-1/2 -translate-y-1/2"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(201,168,76,0.22) 0%, rgba(201,168,76,0.06) 42%, transparent 70%)",
        }}
      />

      {leadReaction ? (
        <div className="hero-float pointer-events-none absolute -left-1 top-10 z-20 hidden opacity-100 transition-opacity duration-300 group-hover:opacity-0 group-focus-within:opacity-0 sm:block lg:-left-6 lg:top-16">
          <ReactionChip
            reaction={leadReaction}
            message={t(`preview.${leadReaction.kind}`)}
          />
        </div>
      ) : null}
      {alex ? (
        <div className="hero-float-delayed pointer-events-none absolute -right-1 top-4 z-20 hidden opacity-100 transition-opacity duration-300 group-hover:opacity-0 group-focus-within:opacity-0 sm:block lg:-right-2 lg:top-8">
          <ReactionChip reaction={alex} message={t(`preview.${alex.kind}`)} />
        </div>
      ) : null}
      {jordan ? (
        <div className="hero-float-slow pointer-events-none absolute bottom-28 left-2 z-20 hidden opacity-100 transition-opacity duration-300 group-hover:opacity-0 group-focus-within:opacity-0 md:block lg:bottom-36 lg:-left-4">
          <ReactionChip
            reaction={jordan}
            message={t(`preview.${jordan.kind}`)}
          />
        </div>
      ) : null}

      {announced ? (
        <div className="hero-float-delayed pointer-events-none absolute right-2 bottom-32 z-20 hidden max-w-[220px] items-center gap-3 rounded-2xl border border-(--mk-gold-border-strong) bg-(--mk-ink-raise) px-3 py-2.5 opacity-100 shadow-[0_16px_40px_rgba(0,0,0,0.45)] transition-opacity duration-300 group-hover:opacity-0 group-focus-within:opacity-0 lg:flex">
          <div
            className="flex h-12 w-9 shrink-0 items-center justify-center rounded-md text-[11px] font-bold text-(--mk-cream-60)"
            style={{ background: announced.poster }}
          >
            {heroPreviewMonogram(announced.name)}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-(--mk-gold)">
              {t("preview.justAnnounced")}
            </p>
            <p className="truncate font-serif text-sm font-bold text-(--mk-cream)">
              {announced.name}
            </p>
          </div>
        </div>
      ) : null}

      <div className="hero-preview-mask perspective-[1600px]">
        <div className="hero-preview-tilt origin-center">
          <ProductWindow
            preview={{
              ...preview,
              nominees: interactive.nominees,
              leaderboard: interactive.leaderboard,
            }}
            onSelect={(name) => {
              setPickedName(name);
              if (!saveNudgeDismissed) setSaveNudgeOpen(true);
            }}
          />
        </div>
      </div>
      {saveNudgeOpen ? (
        <SavePicksNudge
          createPartyPath={createPartyPath}
          onDismiss={() => {
            setSaveNudgeOpen(false);
            setSaveNudgeDismissed(true);
          }}
        />
      ) : null}
    </div>
  );
}

function ProductWindow({
  preview,
  onSelect,
}: {
  preview: HeroPreviewContent;
  onSelect: (name: string) => void;
}) {
  const t = useTranslations("Home");

  return (
    <div className="overflow-hidden rounded-2xl border border-(--mk-cream-whisper) bg-(--mk-ink-soft) shadow-[0_40px_100px_rgba(0,0,0,0.65),0_0_0_1px_rgba(201,168,76,0.1)]">
      <div className="flex items-center gap-3 border-b border-(--mk-white-06) bg-(--mk-white-04) px-4 py-3">
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
        </div>
        <div className="mx-auto flex min-w-0 items-center gap-2 rounded-lg border border-(--mk-white-06) bg-(--mk-ink) px-3 py-1 text-[11px] text-(--mk-cream-35)">
          <BrandMark size={14} className="h-3.5 w-3.5" />
          <span className="truncate">awardparty.app/parties</span>
        </div>
      </div>

      <div className="px-4 pt-4 pb-3 sm:px-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-serif text-lg font-bold text-(--mk-cream) sm:text-xl">
              {t("preview.partyFor", {
                show: preview.showName,
                year: String(preview.showYear),
              })}
            </p>
            <p className="text-[11px] text-(--mk-cream-35)">
              {t("featuredShowLabel", {
                show: preview.showName,
                year: String(preview.showYear),
              })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-full border border-(--mk-cream-whisper) bg-(--mk-cream-surface) px-2.5 py-1 text-[11px] font-medium text-(--mk-cream-60)">
              <Users className="h-3 w-3" />
              {t("preview.watching", { count: String(preview.watchingCount) })}
            </div>
            <div className="flex items-center gap-1.5 rounded-full border border-[rgba(40,200,80,0.3)] bg-[rgba(40,200,80,0.12)] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-(--mk-green)">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-(--mk-green)" />
              {t("live")}
            </div>
          </div>
        </div>

        <div className="mb-4 flex items-center gap-2.5">
          <span className="text-[11px] font-semibold text-(--mk-gold)">
            {t("preview.yourPicks")}
          </span>
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-(--mk-white-08)">
            <div
              className="h-full rounded-full bg-(--mk-gold)"
              style={{
                width: `${Math.round(
                  (preview.pickedCount / preview.totalCategories) * 100,
                )}%`,
              }}
            />
          </div>
          <span className="text-[11px] font-bold tabular-nums text-(--mk-cream-60)">
            {preview.pickedCount}/{preview.totalCategories}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-0 border-t border-(--mk-white-06) lg:grid-cols-[1.15fr_0.85fr]">
        <BallotPanel preview={preview} onSelect={onSelect} />
        <LeaderboardPanel rows={preview.leaderboard} />
      </div>
    </div>
  );
}

function BallotPanel({
  preview,
  onSelect,
}: {
  preview: HeroPreviewContent;
  onSelect: (name: string) => void;
}) {
  const t = useTranslations("Home");
  const categoryLabel =
    preview.categoryName ?? t(`preview.categories.${preview.showKey}`);

  return (
    <div className="px-4 py-4 sm:px-5">
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-(--mk-gold-border-strong) bg-(--mk-gold-bg)">
          <span className="text-[11px] font-bold text-(--mk-gold)">
            {preview.categoryIndex}
          </span>
        </div>
        <p className="min-w-0 flex-1 truncate text-[15px] font-bold tracking-tight text-(--mk-cream-85) sm:text-[17px]">
          {categoryLabel}
        </p>
        <span className="inline-flex items-center gap-1 rounded-full border border-(--mk-green-border) bg-(--mk-green-dim) px-2 py-0.5 text-[10px] font-bold text-(--mk-green)">
          <Check className="h-3 w-3" />
          {t("preview.picked")}
        </span>
      </div>

      <div
        role="radiogroup"
        aria-label={categoryLabel}
        className="flex flex-col gap-1.5"
      >
        {preview.nominees.map((nominee) => (
          <NomineeRow
            key={nominee.name}
            nominee={nominee}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

function NomineeRow({
  nominee,
  onSelect,
}: {
  nominee: HeroPreviewNominee;
  onSelect: (name: string) => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={nominee.selected}
      aria-label={nominee.name}
      onClick={() => onSelect(nominee.name)}
      className={`relative flex w-full cursor-pointer items-center overflow-hidden rounded-xl border-[1.5px] text-left transition-[border-color,background-color] duration-200 hover:border-[rgba(201,168,76,0.28)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--mk-gold) focus-visible:ring-offset-2 focus-visible:ring-offset-(--mk-ink-soft) ${
        nominee.selected
          ? "border-[rgba(201,168,76,0.4)] bg-linear-to-r from-[rgba(201,168,76,0.1)] to-[#161410]"
          : "border-(--mk-white-06) bg-[#161410]"
      }`}
    >
      <div
        className={`absolute top-0 bottom-0 left-0 w-[3px] rounded-l-xl ${
          nominee.selected ? "bg-(--mk-gold)" : "bg-transparent"
        }`}
      />
      <div
        className="flex h-[52px] w-[40px] shrink-0 items-center justify-center self-stretch text-[13px] font-bold text-(--mk-cream-40) sm:h-[58px] sm:w-[44px]"
        style={{ background: nominee.poster }}
      >
        {heroPreviewMonogram(nominee.name)}
      </div>
      <div className="min-w-0 flex-1 px-3 py-2">
        <p
          className={`truncate text-[13px] font-semibold ${
            nominee.selected ? "text-(--mk-gold-light)" : "text-(--mk-cream-80)"
          }`}
        >
          {nominee.name}
        </p>
        {nominee.subtitle ? (
          <p className="truncate text-[11px] text-(--mk-cream-35)">
            {nominee.subtitle}
          </p>
        ) : null}
      </div>
      <div
        className={`mr-3 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-[1.5px] ${
          nominee.selected
            ? "border-(--mk-gold) bg-(--mk-gold) text-(--mk-ink)"
            : "border-(--mk-white-08) bg-(--mk-white-04) text-transparent"
        }`}
      >
        <Check className="h-3 w-3" />
      </div>
    </button>
  );
}

function LeaderboardPanel({ rows }: { rows: HeroPreviewPlayer[] }) {
  const t = useTranslations("Home");

  return (
    <div className="hidden border-t border-(--mk-white-06) bg-(--mk-white-03) px-4 py-4 sm:px-5 lg:block lg:border-t-0 lg:border-l">
      <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-(--mk-gold)">
        <Trophy className="h-3.5 w-3.5" />
        {t("preview.liveStandings")}
      </div>
      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <div
            key={row.name}
            className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 transition-colors duration-200 ${
              row.first
                ? "border-[rgba(201,168,76,0.25)] bg-(--mk-gold-bg)"
                : "border-transparent bg-(--mk-white-03)"
            }`}
          >
            <span
              className={`w-4 text-center font-serif text-xs font-bold ${
                row.first ? "text-(--mk-gold)" : "text-(--mk-cream-ghost)"
              }`}
            >
              {row.rank}
            </span>
            <div
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold text-(--mk-ink)"
              style={{ background: row.bg }}
            >
              {row.initial}
            </div>
            <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-(--mk-cream-80)">
              {row.name}
            </span>
            <div className="hidden h-1 w-12 overflow-hidden rounded-sm bg-(--mk-white-08) sm:block">
              <div
                className={`h-full rounded-sm ${row.first ? "bg-(--mk-gold)" : "bg-(--mk-cream-20)"}`}
                style={{ width: `${row.pct}%` }}
              />
            </div>
            <span
              className={`font-serif text-[12px] font-bold ${
                row.first ? "text-(--mk-gold)" : "text-(--mk-cream)"
              }`}
            >
              {row.score}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SavePicksNudge({
  createPartyPath,
  onDismiss,
}: {
  createPartyPath: string;
  onDismiss: () => void;
}) {
  const t = useTranslations("Home");

  if (typeof document === "undefined") return null;

  const signedOutHref = heroPreviewCreatePartyHref(createPartyPath, false);
  const signedInHref = heroPreviewCreatePartyHref(createPartyPath, true);

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div
        role="status"
        className="pointer-events-auto flex w-full max-w-lg animate-[marketing-fade-up_420ms_ease-out] flex-col gap-3 rounded-2xl border border-(--mk-gold-border-strong) bg-(--mk-ink-raise) px-4 py-3 shadow-[0_16px_40px_rgba(0,0,0,0.5)] motion-reduce:animate-none sm:flex-row sm:items-center"
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <PartyPopper className="h-5 w-5 shrink-0 text-(--mk-gold)" aria-hidden />
          <p className="text-sm font-semibold text-(--mk-cream)">
            {t("preview.savePicks")}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:shrink-0">
          <SignedOut>
            <Link
              href={signedOutHref}
              className="inline-flex cursor-pointer items-center rounded-lg bg-(--mk-gold) px-3 py-2 text-sm font-semibold text-(--mk-ink) no-underline transition-colors hover:bg-(--mk-gold-light)"
            >
              {t("preview.createYourParty")}
            </Link>
          </SignedOut>
          <SignedIn>
            <Link
              href={signedInHref}
              className="inline-flex cursor-pointer items-center rounded-lg bg-(--mk-gold) px-3 py-2 text-sm font-semibold text-(--mk-ink) no-underline transition-colors hover:bg-(--mk-gold-light)"
            >
              {t("preview.createYourParty")}
            </Link>
          </SignedIn>
          <button
            type="button"
            aria-label={t("preview.dismissSavePicks")}
            onClick={onDismiss}
            className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-(--mk-cream-60) transition-colors hover:bg-(--mk-white-06) hover:text-(--mk-cream)"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ReactionChip({
  reaction,
  message,
}: {
  reaction: HeroPreviewReaction;
  message: string;
}) {
  return (
    <div className="flex items-center gap-2 rounded-full border border-(--mk-cream-whisper) bg-(--mk-ink-raise)/95 px-2 py-1.5 shadow-[0_12px_32px_rgba(0,0,0,0.4)] backdrop-blur-sm">
      <div
        className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold text-(--mk-ink)"
        style={{ background: reaction.bg }}
      >
        {reaction.initial}
      </div>
      <p className="pr-1.5 text-[12px] font-medium whitespace-nowrap text-(--mk-cream-85)">
        {message}
      </p>
    </div>
  );
}
